import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { differenceInHours } from 'date-fns';
/* import { differenceInHours } from 'date-fns'; */ // same day validation off
import services from 'services';
import { RootState } from 'types/store';

export interface SetPaymentMethodArgs {
    cartId: string;
    paymentInfo: PaymentInfo[];
}

export const setPaymentMethod = createAsyncThunk(
    'orbis/setPayment',
    async (dummy: boolean, { getState }) => {
        const { cart, billing, user } = getState() as RootState;
        const cartData = cart.data;
        const billingData = billing.data;

        if (cartData && billingData && user) {
            const resp = await services.cart.setPayment(
                cartData.id,
                [
                    {
                        documentType: billingData.documentType,
                        paymentMethod: 'Orbis',
                        billingInfo: {
                            business: billingData.billingInfo.business,
                            customer: billingData.billingInfo.customer,
                            address: billingData.billingInfo.address,
                        },
                        amount: cartData.grandTotal['discount'] || cartData.grandTotal['master'],
                        customFields: {},
                    },
                ],
                { dummy, paymentMethod: 'Orbis' },
            );
            if (resp) {
                return resp.cart;
            }
            return;
        } else {
            throw new Error('Datos incompletos para usar Pago en Efectivo');
        }
    },
);

export const checkOrbisAvailability = createAsyncThunk(
    'orbis/checkAvailability',
    async (_, { getState }) => {
        const { cart, paymentAvailability, rule } = getState() as RootState;
        const cashAvailability = paymentAvailability.data?.find((pa) => pa.paymentType === 'cash')
            ?.processorAvaialable;

        if (!cashAvailability) {
            return false;
        }

        const { onlyProduct1p, sameDay } = rule.data.orbisRules;
        if (onlyProduct1p) {
            const products = cart.data?.products ?? [];
            for (let i = 0; i < products.length; i++) {
                if (+products[i].details.sellerId !== 1) {
                    return false;
                }
            }
        }

        if (!sameDay) {
            if (cart.data?.shippingInfo?.length) {
                const dateFrom = cart.data?.shippingInfo[0].dateFrom || '';
                if (differenceInHours(new Date(dateFrom), new Date()) <= 24 || dateFrom === '') {
                    return false;
                }
                return true;
            }
        }
        return true;
    },
);

interface PaymentInfo {
    cip: number;
    amount: number;
    cipUrl: string;
    currency: string;
    dateExpiry: string;
    installments: number;
    transactionCode: string;
}

export interface OrbisData {
    userName: string;
    sessionId: string;
    userEmail: string;
    userPhone: string;
    adminEmail: string;
    userUbigeo: string;
    paymentInfo: PaymentInfo;
    userCountry: string;
    userLastName: string;
    additionalData: string;
    paymentConcept: string;
    userCodeCountry: string;
    userDocumentType: string;
    userDocumentNumber: string;
}

export interface OrbisState {
    status: 'idle' | 'pending' | 'ok' | 'error';
    data: OrbisData | null;
    error: SerializedError | null;
    available: boolean;
}

const initialState: OrbisState = {
    status: 'idle',
    data: null,
    error: null,
    available: true,
};

export const slice = createSlice({
    name: 'orbis',
    initialState: initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(setPaymentMethod.pending, (state: OrbisState) => {
            state.status = 'pending';
        });
        builder.addCase(setPaymentMethod.fulfilled, (state: OrbisState, { payload }) => {
            state.status = 'ok';
            if (payload) {
                state.data = {
                    userName: payload.paymentInfo?.[0].customFields?.userName,
                    sessionId: payload.paymentInfo?.[0].customFields?.sessionId,
                    userEmail: payload.paymentInfo?.[0].customFields?.userEmail,
                    userPhone: payload.paymentInfo?.[0].customFields?.userPhone,
                    adminEmail: payload.paymentInfo?.[0].customFields?.adminEmail,
                    userUbigeo: payload.paymentInfo?.[0].customFields?.userUbigeo,
                    paymentInfo: {
                        amount: payload.paymentInfo?.[0].customFields?.paymentInfo.amount,
                        cip: payload.paymentInfo?.[0].customFields?.paymentInfo.cip,
                        cipUrl: payload.paymentInfo?.[0].customFields?.paymentInfo.cipUrl,
                        currency: payload.paymentInfo?.[0].customFields?.paymentInfo.currency,
                        dateExpiry: payload.paymentInfo?.[0].customFields?.paymentInfo.dateExpiry,
                        installments:
                            payload.paymentInfo?.[0].customFields?.paymentInfo.installments,
                        transactionCode:
                            payload.paymentInfo?.[0].customFields?.paymentInfo.transactionCode,
                    },
                    userCountry: payload.paymentInfo?.[0].customFields?.userCountry,
                    userLastName: payload.paymentInfo?.[0].customFields?.userLastName,
                    additionalData: payload.paymentInfo?.[0].customFields?.additionalData,
                    paymentConcept: payload.paymentInfo?.[0].customFields?.paymentConcept,
                    userCodeCountry: payload.paymentInfo?.[0].customFields?.userCodeCountry,
                    userDocumentType: payload.paymentInfo?.[0].customFields?.userDocumentType,
                    userDocumentNumber: payload.paymentInfo?.[0].customFields?.userDocumentNumber,
                };
            }
            state.error = null;
        });
        builder.addCase(setPaymentMethod.rejected, (state: OrbisState, action) => {
            state.status = 'error';
            state.data = null;
            state.error = action.error;
        });
        builder.addCase(checkOrbisAvailability.fulfilled, (state: OrbisState, { payload }) => {
            state.available = payload;
        });
        builder.addCase(checkOrbisAvailability.rejected, (state: OrbisState) => {
            state.available = false;
        });
    },
});
